import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

export default function SimilarityScalePreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        Skala podobieństwa <InlineMath math="k" /> mówi, ile razy zmieniają się
        wszystkie <strong>długości</strong>: boki, krawędzie, wysokości, promienie
        oraz obwody. Jeśli przechodzimy od figury mniejszej do większej, to
        odpowiadające długości spełniają zależność:
      </p>
      <BlockMath math={String.raw`\frac{a_2}{a_1}=k`} />
      <p>
        Pole ma dwa wymiary, dlatego zmienia się <InlineMath math="k^2" /> razy.
        Objętość ma trzy wymiary, więc zmienia się <InlineMath math="k^3" /> razy.
      </p>
      <BlockMath
        math={String.raw`\frac{L_2}{L_1}=k,\qquad \frac{P_2}{P_1}=k^2,\qquad \frac{V_2}{V_1}=k^3`}
      />
      <p>
        Pomocna analogia to mapa w skali <InlineMath math="1:100" />: jeden
        centymetr na mapie odpowiada stu centymetrom w rzeczywistości. Z mapy
        do rzeczywistości długość mnożymy przez <InlineMath math="100" />;
        w przeciwnym kierunku skala podobieństwa wynosi <InlineMath math="k=\frac1{100}" />.
      </p>
      <p>
        Zawsze najpierw ustalamy <strong>kierunek porównania</strong>. Jeżeli
        liczymy większą figurę na podstawie mniejszej, używamy
        <InlineMath math="k>1" />. Przy przejściu od większej do mniejszej
        otrzymujemy skalę odwrotną <InlineMath math="0<k<1" />. Przykładowo:
        jeżeli figura <InlineMath math="B" /> jest obrazem figury <InlineMath math="A" />
        w skali <InlineMath math="k=\frac13" />, to każda długość i cały obwód
        figury <InlineMath math="B" /> są trzy razy mniejsze.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Obwody i skala",
      description: (
        <span>
          Obwód figury <InlineMath math="A" /> wynosi <InlineMath math="30\,\mathrm{cm}" />,
          a obwód podobnej figury <InlineMath math="B" /> wynosi <InlineMath math="10\,\mathrm{cm}" />.
        </span>
      ),
      content: (
        <div>
          <BlockMath math={String.raw`k=\frac{L_B}{L_A}=\frac{10}{30}=\frac13`} />
          <p>Obwód jest wielkością jednowymiarową, dlatego stosunek obwodów jest równy skali <InlineMath math="k" />.</p>
        </div>
      ),
    },
    {
      label: "Przykład 2",
      summary: "Od obwodów do pól",
      description: (
        <span>
          Obwody podobnych figur wynoszą <InlineMath math="10\,\mathrm{cm}" /> i{" "}
          <InlineMath math="30\,\mathrm{cm}" />, a pole mniejszej figury to{" "}
          <InlineMath math="4\,\mathrm{cm}^2" />.
        </span>
      ),
      content: (
        <div>
          <BlockMath math={String.raw`k=\frac{30}{10}=3`} />
          <BlockMath math={String.raw`P_2=k^2P_1=3^2\cdot4=36\,\mathrm{cm}^2`} />
          <p>Obwody dają skalę liniową <InlineMath math="k" />, ale pole zmienia się <InlineMath math="k^2" /> razy.</p>
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Skala wyznaczona z pól",
      description: (
        <span>
          Pola figur podobnych wynoszą <InlineMath math="5\,\mathrm{cm}^2" /> i
          <InlineMath math="45\,\mathrm{cm}^2" />.
        </span>
      ),
      content: (
        <div>
          <BlockMath math={String.raw`k^2=\frac{45}{5}=9`} />
          <BlockMath math={String.raw`k=\sqrt9=3`} />
          <p>Gdy znamy stosunek pól, skalę długości otrzymujemy przez wyciągnięcie pierwiastka.</p>
        </div>
      ),
    },
    {
      label: "Przykład 4",
      summary: "Od pól powierzchni do objętości",
      description: (
        <span>
          Pola powierzchni podobnych brył wynoszą <InlineMath math="5\,\mathrm{cm}^2" /> i{" "}
          <InlineMath math="20\,\mathrm{cm}^2" />, a objętość mniejszej wynosi{" "}
          <InlineMath math="5\,\mathrm{cm}^3" />.
        </span>
      ),
      content: (
        <div>
          <BlockMath math={String.raw`k=\sqrt{\frac{20}{5}}=\sqrt4=2`} />
          <BlockMath math={String.raw`V_2=k^3V_1=2^3\cdot5=40\,\mathrm{cm}^3`} />
          <p>Najpierw z pól powierzchni odzyskujemy <InlineMath math="k" />, a dopiero potem stosujemy <InlineMath math="k^3" /> do objętości.</p>
        </div>
      ),
    },
  ];

  return (
    <TheoryExamplesRunner
      title="Skala podobieństwa"
      intro={intro}
      examples={examples}
    />
  );
}
